import type { ChangeEvent } from "react";
import React, { useCallback, useEffect, useState } from "react";

import { CustomInput } from "../../../../../components/CustomInput/CustomInput";
import { CustomModal } from "../../../../../components/CustomModal";
import type { IResponseUser } from "../../../../../hooks/reactQueryHooks/useUsers";
import { usePatchUser } from "../../../../../hooks/reactQueryHooks/useUsers";
import { useDisableSubmit } from "../../../../../hooks/useDisableSubmit";
import type { IEditUser } from "../../MembersTable/MembersTable";
import { EditMemberFooter } from "./EditMemberFooter";

interface IProps {
  member: IEditUser | null;
  closeModal: () => void;
  updateMember: (member: IResponseUser) => void;
}

const EditMemberModal: React.FC<IProps> = ({
  member,
  closeModal,
  updateMember,
}) => {
  const [formValues, setFormValues] = useState<IEditUser | null>(null);
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  } | null>(null);
  const [showError, setShowError] = useState<boolean>(false);

  const mutation = usePatchUser();

  const { isDisabled, setInitialValue, onUpdateValues } = useDisableSubmit();

  useEffect(() => {
    setFormValues(member);
    setInitialValue(member);
  }, [member, setInitialValue]);

  const editMember = useCallback(() => {
    mutation.mutate({
      firstName: formValues?.firstName || "",
      lastName: formValues?.lastName || "",
      userId: formValues?.id || "",
    });
  }, [formValues, mutation]);

  useEffect(() => {
    if (mutation.isSuccess) {
      closeModal();
      setInitialValue(null);
      setFormValues(null);
      updateMember(mutation.data);
    }
  }, [
    mutation.isSuccess,
    closeModal,
    setInitialValue,
    updateMember,
    mutation.data,
  ]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (formValues) {
      onUpdateValues({
        ...formValues,
        [name]: value,
      });
      setFormValues({
        ...formValues,
        [name]: value,
      });
    }
  };
  useEffect(() => {
    if (mutation.isError) {
      const validationErrors: {
        [key: string]: string;
      } = {};
      mutation.error.response?.data.forEach((error: any) => {
        if (error.type === "Validation") {
          validationErrors[error.code] = error.description;
          return;
        }
        setShowError(true);
      });
      setValidationErrors(validationErrors);
      return;
    }
    setValidationErrors(null);
  }, [mutation.isError, mutation.error?.response?.data]);

  return (
    <CustomModal
      title="Edit member"
      show={true}
      handleClose={closeModal}
      footer={
        <EditMemberFooter
          disabledSubmitButton={isDisabled || mutation.isLoading || showError}
          cancelUpdate={closeModal}
          update={editMember}
          isProcessing={mutation.isLoading}
        />
      }
      errorMessage={
        showError ? "Something went wrong. Please try again later." : ""
      }
    >
      <div>
        <CustomInput
          label="First name"
          handleChange={handleChange}
          inputValue={formValues?.firstName || ""}
          placeholder="Enter the new member's name"
          name="firstName"
          autoFocus
          errorMessage={{ error: validationErrors?.firstName || "" }}
          maxLength={150}
        />
        <CustomInput
          label="Last name"
          handleChange={handleChange}
          inputValue={formValues?.lastName || ""}
          placeholder="Enter the new member's name"
          name="lastName"
          errorMessage={{ error: validationErrors?.lastName || "" }}
          maxLength={150}
        />
        <CustomInput
          label="Email"
          handleChange={handleChange}
          inputValue={formValues?.email || ""}
          placeholder="Enter the email"
          name="email"
          disabled
        />
      </div>
    </CustomModal>
  );
};

export { EditMemberModal };
